// BHP-1115: write hostname and labels through to Hetzner.
//
// `PATCH /api/vps/{uuid}` (BHP-1104) writes to Hetzner and reads the server
// back, so the response is already the new truth — but three caches hold a copy
// of it: our own row, the collection behind the overviews, and the live state
// from Hetzner. All three are invalidated, because a rename that shows on the
// detail page while the list still says the old name is the same kind of
// half-truth this whole chain was built to remove.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../../../shared/http/httpClient';
import { hydraKeys } from '../../../shared/api/useHydra';
import type { VpsSummary } from '../domain/vpsSummary';
import type { VpsEdit } from '../domain/vpsLabels';
import { vpsDetailKeys } from './vpsDetailQueries';

export function useVpsEdit(uuid: string): {
  save: (edit: VpsEdit) => Promise<VpsSummary>;
  isPending: boolean;
  error: unknown;
  reset: () => void;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (edit: VpsEdit) =>
      httpClient.patch<VpsSummary>(`/vps/${uuid}`, edit),
    // Awaited, like the power actions: the form stays pending until the page
    // can actually show the new values, instead of flicking back to the old
    // ones for a frame.
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: hydraKeys.item('vps', uuid)
        }),
        queryClient.invalidateQueries({
          queryKey: ['hydra', 'vps', 'collection']
        }),
        queryClient.invalidateQueries({
          queryKey: vpsDetailKeys.state(uuid)
        })
      ]);
    }
  });

  return {
    save: (edit: VpsEdit) => mutation.mutateAsync(edit),
    isPending: mutation.isPending,
    error: mutation.error,
    reset: () => {
      mutation.reset();
    }
  };
}
